import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { QUANTITY_OPTIONS, SITE } from "@/lib/site";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({
    nombre: "",
    clinica: "",
    ciudad: "",
    whatsapp: "",
    email: "",
    cantidad_mensual: "",
    mensaje: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.whatsapp || !form.cantidad_mensual) {
      toast.error("Completa los campos requeridos");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      // Meta Pixel — Lead conversion event
      if (typeof window !== "undefined" && typeof window.fbq === "function") {
        try {
          window.fbq("track", "Lead", {
            content_name: "Programa Distribución Clamignon",
            content_category: form.cantidad_mensual || "unspecified",
          });
        } catch (_) {}
      }
      setDone(true);
      toast.success("Solicitud enviada — te contactamos por WhatsApp");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Error al enviar. Intenta nuevamente."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contacto" data-testid="contact-section" className="relative py-20 md:py-28">
      <div className="max-w-[1160px] mx-auto px-4 md:px-6">
        <div className="rounded-[36px] md:rounded-[44px] card-glass overflow-hidden p-6 md:p-14 relative">
          {/* aurora ambient */}
          <div className="pointer-events-none absolute inset-0">
            <div className="aurora-orb" style={{ width: "560px", height: "560px", right: "-160px", top: "-160px", opacity: 0.6 }} />
          </div>

          <div className="relative grid grid-cols-12 gap-8 md:gap-12 items-start">
            {/* LEFT copy */}
            <div className="col-span-12 lg:col-span-5">
              <span className="chip mb-5">
                <span className="dot" /> Clínicas preferenciales
              </span>
              <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold text-[#0B0B14] tracking-tight leading-[1.05]">
                Solicita tu <span className="text-[#5A48C9]">evaluación de precio</span> confidencial.
              </h2>
              <p className="mt-6 text-[#3A3A48] leading-relaxed max-w-md">
                Programa exclusivo para clínicas con consumo{" "}
                <span className="text-[#0B0B14] font-semibold">≥ 100 unidades / mes</span>.
                Un ejecutivo Clamignon te llamará por WhatsApp en menos de{" "}
                <span className="text-[#0B0B14] font-semibold">24 horas hábiles</span>{" "}
                con tu tramo de precio, stock disponible y certificado ISP del lote vigente.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-[#1E1E2C]">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#7C6AF5]" /> Sin costo. Sin obligación de compra.</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#7C6AF5]" /> Cotización con precio final por unidad.</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#7C6AF5]" /> Confidencial. No compartimos tu volumen.</li>
              </ul>

              <div className="mt-8 space-y-3">
                <a
                  href={`https://wa.me/${SITE.whatsapp.number}?text=${encodeURIComponent(SITE.whatsapp.message)}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-whatsapp-link"
                  className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-white/70 backdrop-blur border border-black/[0.06] hover:border-emerald-400/60 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                      <FaWhatsapp className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-emerald-700">
                        WhatsApp directo
                      </p>
                      <p className="text-[#0B0B14] text-sm font-semibold">
                        {SITE.whatsapp.display}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#6B6B7D] group-hover:text-[#0B0B14] transition-colors" />
                </a>

                <a
                  href={SITE.brochureUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="contact-brochure-link"
                  className="group flex items-center justify-between gap-4 p-5 rounded-2xl bg-white/70 backdrop-blur border border-black/[0.06] hover:border-[#7C6AF5]/60 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#F1EDFF] border border-[#7C6AF5]/25 flex items-center justify-center">
                      <ArrowRight className="w-4 h-4 text-[#5A48C9] -rotate-45" />
                    </div>
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-[#5A48C9]">
                        Ficha técnica AEGENBIO
                      </p>
                      <p className="text-[#0B0B14] text-sm font-semibold">
                        Ver monografía completa (PDF)
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#6B6B7D] group-hover:text-[#0B0B14] transition-colors" />
                </a>
              </div>
            </div>

            {/* RIGHT form */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="col-span-12 lg:col-span-7"
            >
              {done ? (
                <div
                  data-testid="form-success"
                  className="rounded-3xl bg-white/90 backdrop-blur border border-emerald-200 p-10 md:p-14 text-center"
                >
                  <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-6" />
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-[#0B0B14] mb-4 tracking-tight">
                    Solicitud recibida.
                  </h3>
                  <p className="text-[#3A3A48] max-w-md mx-auto">
                    Un ejecutivo Clamignon te contactará por WhatsApp en menos de 24
                    horas hábiles con lista actualizada y stock disponible.
                  </p>
                  <a
                    href={`https://wa.me/${SITE.whatsapp.number}?text=${encodeURIComponent(SITE.whatsapp.message)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex mt-8 items-center gap-2 px-5 py-3 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    Contactar por WhatsApp
                  </a>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  data-testid="lead-form"
                  className="rounded-3xl bg-white/90 backdrop-blur border border-black/[0.06] p-6 md:p-10 shadow-[0_20px_60px_-30px_rgba(11,11,20,0.15)]"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Nombre" testid="input-nombre" required value={form.nombre} onChange={set("nombre")} placeholder="Dra. María González" />
                    <Field label="Clínica o Particular" testid="input-clinica" value={form.clinica} onChange={set("clinica")} placeholder="Clínica Estética Andes" />
                    <Field label="Ciudad" testid="input-ciudad" value={form.ciudad} onChange={set("ciudad")} placeholder="Santiago" />
                    <Field label="WhatsApp" testid="input-whatsapp" required value={form.whatsapp} onChange={set("whatsapp")} placeholder="+56 9 XXXX XXXX" />
                    <Field label="Email" testid="input-email" required type="email" value={form.email} onChange={set("email")} placeholder="contacto@tuclinica.cl" />
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D]">
                        Cantidad mensual <span className="text-[#7C6AF5]">*</span>
                      </label>
                      <Select
                        value={form.cantidad_mensual}
                        onValueChange={(v) => setForm((s) => ({ ...s, cantidad_mensual: v }))}
                      >
                        <SelectTrigger
                          data-testid="select-cantidad"
                          className="bg-[#F7F5FC] border-black/[0.08] text-[#0B0B14] h-12 rounded-2xl focus:border-[#7C6AF5]"
                        >
                          <SelectValue placeholder="Elige un rango" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-black/10 text-[#0B0B14]">
                          {QUANTITY_OPTIONS.map((q) => (
                            <SelectItem key={q} value={q} className="focus:bg-[#F1EDFF]">
                              {q}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-col gap-1.5">
                    <label className="text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D]">
                      Cuéntanos de ti o tu clínica
                    </label>
                    <textarea
                      data-testid="input-mensaje"
                      value={form.mensaje}
                      onChange={set("mensaje")}
                      rows={4}
                      placeholder="Especialidad, volumen actual, marcas que usas, dudas..."
                      className="w-full bg-[#F7F5FC] border border-black/[0.08] rounded-2xl px-4 py-3 text-[#0B0B14] placeholder:text-[#9A9AAD] focus:border-[#7C6AF5] focus:outline-none focus:ring-0 resize-none"
                    />
                  </div>

                  <div className="mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D]">
                      Solo compras ≥ 100 unidades / mes
                    </p>
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="submit-lead-button"
                      className="btn-pill btn-primary shine text-[15px] px-6 py-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {loading ? "Enviando..." : "Solicitar evaluación confidencial"}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, required, type = "text", value, onChange, placeholder }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[11px] font-semibold uppercase tracking-widest text-[#6B6B7D]">
        {label} {required && <span className="text-[#7C6AF5]">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-[#F7F5FC] border border-black/[0.08] rounded-2xl px-4 h-12 text-[#0B0B14] placeholder:text-[#9A9AAD] focus:border-[#7C6AF5] focus:outline-none focus:ring-0"
      />
    </div>
  );
}
